import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActivityIndicator, Pressable, ScrollView, Text, View } from "react-native";
import MaterialCommunityIcons from "react-native-vector-icons/MaterialCommunityIcons";
import dayjs from "dayjs";
import { DemandeAbsenceProps } from "../DemandeAbsence";
import { DemandeAbsenceApi } from "../../api/DemandeAbsence";
import { ValidationReponseApi } from "../../api/ValidationReponse/ValidationReponseApi";
import { DemandeAbsenceType } from "../../api/DemandeAbsence/types";
import { DemandeDotationType } from "../../api/CET/types";
import { axiosInstance } from "../../api/Middleware";
import { useSessionStore } from "../../lib/Store/sessionStore.component";
import { useNotify } from "../../components/Notification";
import useEnTetePage from "../../components/EnTetePage/EnTetePage";
import { Presences } from "../Planning/Planning.types";
import useEspaceValideurStyle, { couleursValideur } from './EspaceValideur.styles';
import CarteDemande from "./CarteDemande";
import ValideurDetailDemande from "./ValideurDetailDemande";
import ValidationGroupee from "./ValidationGroupee";
import useDemandesTerminees from "./useDemandesTerminees";
import { Decision, DecisionDemande, DemandeAValider, EtatDemande, FiltreDemandes, NbTerminees } from './EspaceValideur.types';
import { cleDemande, estAbsence, estTerminee, repartirDemandes, joursDemandeCet, trierParCreation, texteDuree, textePeriode } from "./EspaceValideur.utils";

// "vide" s'affiche quand il n'y a aucune demande modifiable dans la colonne, "videTerminees" quand aucune n'a de chaîne de validation terminée
const COLONNES: { cle: EtatDemande, libelle: string, couleur: string, fond: string, texte: string, vide: string, videTerminees?: string }[] = [
    { cle: 'attente', libelle: 'À valider', couleur: couleursValideur.orangeFg, fond: couleursValideur.orangeBg, texte: couleursValideur.orangeFg, vide: 'Aucune demande en attente' },
    { cle: 'valide', libelle: 'Validées', couleur: couleursValideur.succes, fond: couleursValideur.vertBg, texte: couleursValideur.vertFg, vide: 'Aucune demande validée modifiable', videTerminees: 'Aucune demande entièrement validée' },
    { cle: 'refuse', libelle: 'Refusées', couleur: couleursValideur.danger, fond: couleursValideur.roseBg, texte: couleursValideur.roseFg, vide: 'Aucune demande refusée modifiable', videTerminees: 'Aucune demande entièrement refusée' },
]

const FILTRES: { cle: FiltreDemandes, libelle: string }[] = [
    { cle: 'tous', libelle: 'Tous' },
    { cle: 'abs', libelle: "Demandes d'absence" },
    { cle: 'cet', libelle: 'Demandes de CET' },
]

const LARGEUR_COLONNE_MOBILE = 290;

const AUCUNE_TERMINEE: NbTerminees = { valide: 0, refuse: 0 };
const REPLIEES: Record<EtatDemande, boolean> = { attente: false, valide: false, refuse: false };

export default function EspaceValideur({ navigation }: DemandeAbsenceProps) {
    const { getCurrentInfos } = useSessionStore();
    const { currentLogin, currentEntite } = getCurrentInfos();
    const { styles, isResponsive } = useEspaceValideurStyle();
    const notify = useNotify();
    // useNotify renvoie un nouvel objet à chaque rendu : la ref évite de relancer le chargement des demandes
    const notifyRef = useRef(notify);
    notifyRef.current = notify;
    const heuresParJour = currentEntite?.collectivitegr?.dureeJourEnHeureCet || 7;
    // L'année sélectionnée dans l'en-tête filtre les demandes d'absence comme les demandes de CET
    const { EnTete, currentDate } = useEnTetePage({
        text: 'Espace valideur',
        periodGestion: { period: 'year', defaultDate: dayjs() },
    });
    const annee = currentDate.year();

    const [demandes, setDemandes] = useState<DemandeAbsenceType[]>([]);
    const [cet, setCet] = useState<DemandeDotationType[]>([]);
    const [presences, setPresences] = useState<Presences>();
    // États des demandes renvoyés par le serveur (à valider, acceptées, refusées)
    const [etatsServeur, setEtatsServeur] = useState<Record<string, EtatDemande>>({});
    // Décisions prises pendant la session : les demandes traitées passent dans la colonne correspondante
    const [decisions, setDecisions] = useState<Record<string, DecisionDemande>>({});
    // Demandes dont la chaîne de validation est terminée : seul leur nombre est connu
    // tant que la colonne "Validées" ou "Refusées" correspondante n'a pas été dépliée
    const [totauxAbs, setTotauxAbs] = useState<NbTerminees>(AUCUNE_TERMINEE);
    const [totauxCet, setTotauxCet] = useState<NbTerminees>(AUCUNE_TERMINEE);
    const [deplies, setDeplies] = useState<Record<EtatDemande, boolean>>(REPLIEES);
    const [filtre, setFiltre] = useState<FiltreDemandes>('tous');
    const [vues, setVues] = useState<Record<EtatDemande, boolean>>({ attente: true, valide: true, refuse: true });
    const [selection, setSelection] = useState<{ demande: DemandeAValider, choix?: Decision }>();
    // Sélection multiple des demandes en attente (clés de demande)
    const [cochees, setCochees] = useState<Set<string>>(new Set());
    const [decisionGroupee, setDecisionGroupee] = useState<Decision>();

    const parametres = `annee=${annee}`;
    const identifiantAgent = currentLogin?.identifiant || 0;
    const replierSurErreur = (decision: Decision, libelle: string) => () => {
        notifyRef.current.error(`Les demandes ${libelle} ${decision === 'valide' ? 'validées' : 'refusées'} n'ont pas pu être chargées.`);
        setDeplies((old) => ({ ...old, [decision]: false }));
    }
    // Chaque liste n'est chargée qu'au dépliage de sa colonne, et seulement si le filtre courant l'affiche
    const absencesValidees = useDemandesTerminees<DemandeAbsenceType>(
        `/personnel/personnel/demandesabsences/${identifiantAgent}/validees?${parametres}`,
        deplies.valide && filtre !== 'cet',
        replierSurErreur('valide', "d'absence"),
    );
    const absencesRefusees = useDemandesTerminees<DemandeAbsenceType>(
        `/personnel/personnel/demandesabsences/${identifiantAgent}/refusees?${parametres}`,
        deplies.refuse && filtre !== 'cet',
        replierSurErreur('refuse', "d'absence"),
    );
    const cetValidees = useDemandesTerminees<DemandeDotationType>(
        `/personnel/personnel/demandesdotations/${identifiantAgent}/validees?${parametres}`,
        deplies.valide && filtre !== 'abs',
        replierSurErreur('valide', 'de CET'),
    );
    const cetRefusees = useDemandesTerminees<DemandeDotationType>(
        `/personnel/personnel/demandesdotations/${identifiantAgent}/refusees?${parametres}`,
        deplies.refuse && filtre !== 'abs',
        replierSurErreur('refuse', 'de CET'),
    );

    // L'état d'une demande terminée est celui de la route qui l'a renvoyée
    const etatsTerminees = useMemo(() => {
        const etats: Record<string, EtatDemande> = {};
        const listes: [DemandeAValider[] | undefined, Decision][] = [
            [absencesValidees, 'valide'],
            [cetValidees, 'valide'],
            [absencesRefusees, 'refuse'],
            [cetRefusees, 'refuse'],
        ];
        listes.forEach(([liste, etat]) => liste?.forEach((demande) => {
            etats[cleDemande(demande)] = etat;
        }));
        return etats;
    }, [absencesRefusees, absencesValidees, cetRefusees, cetValidees])

    const etatDe = useCallback((demande: DemandeAValider): EtatDemande => {
        const cle = cleDemande(demande);
        return decisions[cle]?.etat ?? etatsServeur[cle] ?? etatsTerminees[cle] ?? 'attente';
    }, [decisions, etatsServeur, etatsTerminees])

    const demandesVisibles = useMemo<DemandeAValider[]>(() => {
        if(filtre === 'abs'){
            return trierParCreation(demandes);
        }
        if(filtre === 'cet'){
            return trierParCreation(cet);
        }
        return trierParCreation([...demandes, ...cet]);
    }, [cet, demandes, filtre])

    const colonnes = useMemo(() => COLONNES.map((colonne) => {
        const dansColonne = demandesVisibles.filter((demande) => etatDe(demande) === colonne.cle);
        const enCours = dansColonne.filter((demande) => !estTerminee(demande));
        if(colonne.cle === 'attente'){
            return { ...colonne, enCours, terminees: [], nbTerminees: 0, chargement: false, total: enCours.length };
        }
        // Les demandes terminées sont proposées après celles encore en cours de validation, une fois chargées.
        // Tant qu'une liste n'est pas chargée, seul le total renvoyé par la route "a-valider" est connu.
        const valide = colonne.cle === 'valide';
        const sources: { demandes?: DemandeAValider[], total: number }[] = [
            ...(filtre !== 'cet' ? [{ demandes: valide ? absencesValidees : absencesRefusees, total: totauxAbs[colonne.cle] }] : []),
            ...(filtre !== 'abs' ? [{ demandes: valide ? cetValidees : cetRefusees, total: totauxCet[colonne.cle] }] : []),
        ];
        const chargees = sources.flatMap((source) => source.demandes ?? []);
        const clesChargees = new Set(chargees.map(cleDemande));
        // Demandes dont la chaîne s'est terminée pendant la session : absentes des listes et des totaux déjà reçus
        const termineesSession = dansColonne.filter((demande) => estTerminee(demande) && !clesChargees.has(cleDemande(demande)));
        const terminees = trierParCreation([...chargees, ...termineesSession]);
        const nbTerminees = sources.reduce((total, source) => total + (source.demandes?.length ?? source.total), 0) + termineesSession.length;
        const chargement = deplies[colonne.cle] && sources.some((source) => !source.demandes);
        return { ...colonne, enCours, terminees, nbTerminees, chargement, total: enCours.length + nbTerminees };
    }), [absencesRefusees, absencesValidees, cetRefusees, cetValidees, demandesVisibles, deplies, etatDe, filtre, totauxAbs, totauxCet])

    // Seules les demandes en attente affichées par le filtre courant sont traitées en groupe
    const attenteVisibles = useMemo(() => colonnes[0].enCours, [colonnes]);
    const demandesCochees = useMemo(() => (
        attenteVisibles.filter((demande) => cochees.has(cleDemande(demande)))
    ), [attenteVisibles, cochees])
    const toutesCochees = attenteVisibles.length > 0 && demandesCochees.length === attenteVisibles.length;

    const nbAttente = useMemo(() => ({
        tous: demandes.filter((d) => etatDe(d) === 'attente').length + cet.filter((d) => etatDe(d) === 'attente').length,
        abs: demandes.filter((d) => etatDe(d) === 'attente').length,
        cet: cet.filter((d) => etatDe(d) === 'attente').length,
    }), [cet, demandes, etatDe])

    const resumeJour = useMemo(() => {
        if(!presences){
            return [];
        }
        const { presents, absents, nonTravailles } = presences.membres;
        return [
            { libelle: `${presents.length} présent${presents.length > 1 ? 's' : ''}`, fond: couleursValideur.vertBg, texte: couleursValideur.vertFg },
            { libelle: `${absents.length} absent${absents.length > 1 ? 's' : ''}`, fond: couleursValideur.roseBg, texte: couleursValideur.roseFg },
            { libelle: `${nonTravailles.length} non travaillé`, fond: couleursValideur.grey100, texte: couleursValideur.grey600 },
        ];
    }, [presences])

    const nbAgents = useMemo(() => {
        return presences
            ? presences.membres.presents.length + presences.membres.absents.length + presences.membres.nonTravailles.length
            : 0;
    }, [presences])

    const joursCetEnAttente = useMemo(() => {
        if(!selection){
            return 0;
        }
        return cet
            .filter((demande) => demande.identifiantAgent === selection.demande.identifiantAgent && etatDe(demande) === 'attente')
            .reduce((total, demande) => total + joursDemandeCet(demande, heuresParJour), 0);
    }, [cet, etatDe, heuresParJour, selection])

    const openPlanning = useCallback(() => {
        setSelection(undefined);
        navigation.navigate('Planning')
    }, [navigation])

    const handleOpen = useCallback((demande: DemandeAValider, choix?: Decision) => setSelection({ demande, choix }), [])
    const handleClose = useCallback(() => setSelection(undefined), [])

    const toggleVue = useCallback((cle: EtatDemande) => () => {
        setVues((old) => {
            const nouvelles = { ...old, [cle]: !old[cle] };
            // Au moins une colonne reste affichée
            return Object.values(nouvelles).some(Boolean) ? nouvelles : old;
        })
    }, [])

    const toggleTerminees = useCallback((cle: Decision) => () => {
        setDeplies((old) => ({ ...old, [cle]: !old[cle] }));
    }, [])

    const toggleCochee = useCallback((demande: DemandeAValider) => {
        setCochees((old) => {
            const nouvelles = new Set(old);
            const cle = cleDemande(demande);
            if(nouvelles.has(cle)){
                nouvelles.delete(cle);
            } else {
                nouvelles.add(cle);
            }
            return nouvelles;
        })
    }, [])

    const toggleToutes = useCallback(() => {
        setCochees(toutesCochees ? new Set() : new Set(attenteVisibles.map(cleDemande)));
    }, [attenteVisibles, toutesCochees])

    const viderSelection = useCallback(() => setCochees(new Set()), [])
    const ouvrirValidationGroupee = useCallback(() => setDecisionGroupee('valide'), [])
    const ouvrirRefusGroupe = useCallback(() => setDecisionGroupee('refuse'), [])
    const fermerDecisionGroupee = useCallback(() => setDecisionGroupee(undefined), [])

    // Enregistre la décision puis déplace la demande dans la colonne correspondant au statut renvoyé
    const envoyerDecision = useCallback((demande: DemandeAValider, decision: Decision, commentaire: string) => {
        // Une décision déjà prise ne peut pas être renvoyée
        if(etatDe(demande) === decision){
            return Promise.reject(new Error('Décision déjà prise'));
        }
        const identifiantDemande = estAbsence(demande)
            ? { identifiantDemandeAbsence: demande.identifiant }
            : { identifiantDemandeDotation: demande.identifiant };
        return ValidationReponseApi.updateValidationReponse({
            ...identifiantDemande,
            identifiantAgent: currentLogin?.identifiant || 0,
            reponse: decision === 'valide' ? 3 : 2,
            libelle: commentaire || undefined,
        }).then((response) => {
            const cle = cleDemande(demande);
            // 1 / -1 : la chaîne est terminée (acceptée / refusée), 0 : la demande suit la décision du valideur
            const statut = response.data?.statut ?? 0;
            let etat: Decision = decision;
            if(statut === 1){
                etat = 'valide';
            } else if(statut === -1){
                etat = 'refuse';
            }
            setDecisions((old) => ({ ...old, [cle]: { etat, commentaire } }));
            if(statut !== 0){
                // La demande passe dans la section "Chaîne de validation terminée" et n'est plus modifiable
                if(estAbsence(demande)){
                    setDemandes((old) => old.map((d) => d.identifiant === demande.identifiant ? { ...d, statut } : d));
                } else {
                    setCet((old) => old.map((d) => d.identifiant === demande.identifiant ? { ...d, statut } : d));
                }
            }
            setCochees((old) => {
                if(!old.has(cle)){
                    return old;
                }
                const nouvelles = new Set(old);
                nouvelles.delete(cle);
                return nouvelles;
            });
        })
    }, [currentLogin?.identifiant, etatDe])

    const handleConfirmGroupe = useCallback((decision: Decision, commentaire: string) => {
        const aTraiter = demandesCochees;
        return Promise.allSettled(aTraiter.map((demande) => envoyerDecision(demande, decision, commentaire)))
            .then((resultats) => {
                const nbEchecs = resultats.filter((resultat) => resultat.status === 'rejected').length;
                const nbSucces = resultats.length - nbEchecs;
                const action = decision === 'valide' ? 'acceptée' : 'refusée';
                if(nbSucces){
                    notify.success(`${nbSucces} demande${nbSucces > 1 ? 's' : ''} ${action}${nbSucces > 1 ? 's' : ''}. Les agents en sont notifiés.`);
                }
                if(nbEchecs){
                    // Les demandes en échec restent cochées pour pouvoir relancer le traitement
                    notify.error(`${nbEchecs} demande${nbEchecs > 1 ? 's' : ''} n'${nbEchecs > 1 ? 'ont' : 'a'} pas pu être traitée${nbEchecs > 1 ? 's' : ''}. Veuillez réessayer.`);
                } else {
                    setDecisionGroupee(undefined);
                }
            })
    }, [demandesCochees, envoyerDecision, notify])

    const notifierDecision = useCallback((demande: DemandeAValider, decision: Decision) => {
        const objet = estAbsence(demande)
            ? `La demande de ${(demande.natureAbsence?.libelle || '').toLowerCase()} ${textePeriode(demande).toLowerCase()}`
            : `La demande d'épargne CET de ${texteDuree(demande, heuresParJour)}`;
        notify.success(`${objet} de ${demande.agent?.nomClassement || "l'agent"} a été ${decision === 'valide' ? 'acceptée' : 'refusée'}. L'agent en est notifié.`);
    }, [heuresParJour, notify])

    const handleConfirm = useCallback((decision: Decision, commentaire: string) => {
        if(!selection){
            return Promise.resolve();
        }
        const { demande } = selection;
        return envoyerDecision(demande, decision, commentaire).then(() => {
            setSelection(undefined);
            notifierDecision(demande, decision);
        }).catch(() => {
            notify.error("La décision n'a pas pu être enregistrée. Veuillez réessayer.");
        })
    }, [envoyerDecision, notifierDecision, notify, selection])

    // Validation directe depuis la carte, sans commentaire
    const handleValiderDirect = useCallback((demande: DemandeAValider) => {
        return envoyerDecision(demande, 'valide', '').then(() => {
            notifierDecision(demande, 'valide');
        }).catch(() => {
            notify.error("La décision n'a pas pu être enregistrée. Veuillez réessayer.");
        })
    }, [envoyerDecision, notifierDecision, notify])

    useEffect(() => {
        // Ignore la réponse d'une année précédemment sélectionnée si elle arrive après celle de l'année courante
        let anneeCourante = true;
        setDemandes([]);
        setTotauxAbs(AUCUNE_TERMINEE);
        DemandeAbsenceApi.getDemandesAValider(currentLogin?.identifiant || 0, annee).then((data) => {
            if(!anneeCourante){
                return;
            }
            const { demandes: absences, etats, totaux } = repartirDemandes(data);
            setDemandes(absences);
            setTotauxAbs(totaux);
            setEtatsServeur((old) => ({ ...old, ...etats }));
        }).catch(() => {
            if(anneeCourante){
                notifyRef.current.error(`Les demandes d'absence de ${annee} n'ont pas pu être chargées.`);
            }
        })
        return () => {
            anneeCourante = false;
        };
    }, [annee, currentLogin?.identifiant])

    useEffect(() => {
        let anneeCourante = true;
        setCet([]);
        setTotauxCet(AUCUNE_TERMINEE);
        axiosInstance.get(`/personnel/personnel/demandesdotations/${currentLogin?.identifiant}/a-valider?annee=${annee}`).then((response) => {
            if(!anneeCourante){
                return;
            }
            const { demandes: dotations, etats, totaux } = repartirDemandes<DemandeDotationType>(response.data);
            setCet(dotations);
            setTotauxCet(totaux);
            setEtatsServeur((old) => ({ ...old, ...etats }));
        }).catch(() => {
            if(anneeCourante){
                notifyRef.current.error(`Les demandes de CET de ${annee} n'ont pas pu être chargées.`);
            }
        })
        return () => {
            anneeCourante = false;
        };
    }, [annee, currentLogin?.identifiant])

    useEffect(() => {
        axiosInstance.get(`/personnel/personnel/plageshoraires/monplanning?identifiantAgent=${currentLogin?.identifiant}&dateReference=${dayjs().format('YYYY-MM-DD')}&periode=jour`)
            .then((response) => setPresences(response.data))
    }, [currentLogin?.identifiant])

    const renderCarte = (demande: DemandeAValider, etat: EtatDemande) => (
        <CarteDemande
            key={cleDemande(demande)}
            demande={demande}
            etat={etat}
            commentaire={decisions[cleDemande(demande)]?.commentaire}
            onOpen={handleOpen}
            onValider={handleValiderDirect}
            selectionnee={cochees.has(cleDemande(demande))}
            onToggleSelection={toggleCochee}
        />
    );

    const colonnesAffichees = colonnes.filter((colonne) => vues[colonne.cle]);
    const contenuKanban = colonnesAffichees.map((colonne) => (
        <View key={colonne.cle} style={styles.colonne}>
            <View style={[styles.colonneBarre, { backgroundColor: colonne.couleur }]}/>
            <View style={styles.colonneEnTete}>
                {colonne.cle === 'attente' && colonne.enCours.length > 0 && (
                    <Pressable
                        onPress={toggleToutes}
                        hitSlop={8}
                        accessibilityRole="checkbox"
                        accessibilityLabel="Tout sélectionner"
                        accessibilityState={{ checked: toutesCochees }}
                        style={[styles.caseACocher, demandesCochees.length > 0 && styles.caseACocherActive]}
                    >
                        {demandesCochees.length > 0 && (
                            <MaterialCommunityIcons name={toutesCochees ? 'check' : 'minus'} size={13} color={couleursValideur.blanc}/>
                        )}
                    </Pressable>
                )}
                <Text style={styles.colonneTitre}>{colonne.libelle}</Text>
                <View style={[styles.compteur, { backgroundColor: colonne.fond }]}>
                    <Text style={[styles.compteurTexte, { color: colonne.texte }]}>{colonne.total}</Text>
                </View>
            </View>
            <View style={styles.colonneContenu}>
                {colonne.cle === 'attente' ? (
                    colonne.enCours.length === 0 ? (
                        <View style={styles.colonneVide}>
                            <Text style={styles.colonneVideTexte}>{colonne.vide}</Text>
                        </View>
                    ) : colonne.enCours.map((demande) => renderCarte(demande, colonne.cle))
                ) : (
                    <>
                        {colonne.enCours.length === 0 ? (
                            <View style={[styles.colonneVide, styles.sectionVide]}>
                                <Text style={styles.colonneVideTexte}>{colonne.vide}</Text>
                            </View>
                        ) : colonne.enCours.map((demande) => renderCarte(demande, colonne.cle))}
                        {/* Les demandes dont la chaîne de validation est terminée sont regroupées en bas de la colonne */}
                        <View style={styles.termineesBloc}>
                            {colonne.nbTerminees === 0 ? (
                                <View style={[styles.colonneVide, styles.sectionVide]}>
                                    <Text style={styles.colonneVideTexte}>{colonne.videTerminees}</Text>
                                </View>
                            ) : (
                                <Pressable
                                    onPress={toggleTerminees(colonne.cle)}
                                    accessibilityRole="button"
                                    accessibilityState={{ expanded: deplies[colonne.cle] }}
                                    style={styles.terminees}
                                >
                                    <MaterialCommunityIcons name={deplies[colonne.cle] ? 'chevron-up' : 'chevron-down'} size={18} color={couleursValideur.grey600}/>
                                    <Text style={styles.termineesTexte}>Chaîne de validation terminée</Text>
                                    {colonne.chargement
                                        ? <ActivityIndicator size="small" color={couleursValideur.grey600}/>
                                        : (
                                            <View style={[styles.compteur, { backgroundColor: colonne.fond }]}>
                                                <Text style={[styles.compteurTexte, { color: colonne.texte }]}>{colonne.nbTerminees}</Text>
                                            </View>
                                        )}
                                </Pressable>
                            )}
                            {deplies[colonne.cle] && colonne.terminees.map((demande) => renderCarte(demande, colonne.cle))}
                        </View>
                    </>
                )}
            </View>
        </View>
    ));

    return (
        <ScrollView contentContainerStyle={styles.container}>
            {selection && (
                <ValideurDetailDemande
                    demande={selection.demande}
                    etat={etatDe(selection.demande)}
                    choixInitial={selection.choix}
                    joursCetEnAttente={joursCetEnAttente}
                    onConfirm={handleConfirm}
                    onClose={handleClose}
                    onOpenPlanning={openPlanning}
                />
            )}
            {decisionGroupee && demandesCochees.length > 0 && (
                <ValidationGroupee
                    demandes={demandesCochees}
                    choixInitial={decisionGroupee}
                    onConfirm={handleConfirmGroupe}
                    onClose={fermerDecisionGroupee}
                />
            )}

            <View>
                {EnTete}
                {presences && (
                    <Text style={styles.sousTitrePage}>
                        {presences.service?.libelle} · {nbAgents} agent{nbAgents > 1 ? 's' : ''}
                    </Text>
                )}
            </View>

            <Pressable onPress={openPlanning} style={styles.planning}>
                <View style={styles.planningIcone}>
                    <MaterialCommunityIcons name="account-multiple-outline" size={20} color={couleursValideur.bleu}/>
                </View>
                <View style={styles.planningTexte}>
                    <Text style={styles.planningTitre}>Planning du jour</Text>
                    <Text style={styles.planningSousTitre}>
                        {dayjs().format('dddd D MMMM YYYY').replace(/^./, (lettre) => lettre.toUpperCase())} · voir le planning du service
                    </Text>
                </View>
                <View style={styles.planningResume}>
                    {resumeJour.map((resume) => (
                        <View key={resume.libelle} style={[styles.pilule, { backgroundColor: resume.fond }]}>
                            <Text style={[styles.piluleTexte, { color: resume.texte }]}>{resume.libelle}</Text>
                        </View>
                    ))}
                </View>
                {!isResponsive && <MaterialCommunityIcons name="chevron-right" size={18} color={couleursValideur.grey450}/>}
            </Pressable>

            <View style={styles.barreOutils}>
                <View style={styles.barreGauche}>
                    <Text style={styles.sectionTitre}>Demandes à traiter</Text>
                    <View style={styles.vues}>
                        <Text style={styles.vuesLabel}>Afficher :</Text>
                        {colonnes.map((colonne) => {
                            const active = vues[colonne.cle];
                            return (
                                <Pressable
                                    key={colonne.cle}
                                    onPress={toggleVue(colonne.cle)}
                                    style={[styles.vue, {
                                        borderColor: active ? colonne.couleur : couleursValideur.grey300,
                                        backgroundColor: active ? colonne.fond : couleursValideur.blanc,
                                    }]}
                                >
                                    <MaterialCommunityIcons
                                        name={active ? 'eye-outline' : 'eye-off-outline'}
                                        size={14}
                                        color={active ? couleursValideur.grey800 : couleursValideur.grey600}
                                    />
                                    <View style={[styles.pastille, { backgroundColor: colonne.couleur }]}/>
                                    <Text style={[styles.vueTexte, { color: active ? couleursValideur.grey800 : couleursValideur.grey600 }]}>
                                        {colonne.libelle} · {colonne.total}
                                    </Text>
                                </Pressable>
                            )
                        })}
                    </View>
                </View>
                <View style={styles.filtres}>
                    {FILTRES.map((option) => {
                        const actif = filtre === option.cle;
                        return (
                            <Pressable
                                key={option.cle}
                                onPress={() => setFiltre(option.cle)}
                                style={[styles.filtre, actif && styles.filtreActif]}
                            >
                                <Text style={[styles.filtreTexte, actif && styles.filtreTexteActif]}>
                                    {option.libelle} · {nbAttente[option.cle]}
                                </Text>
                            </Pressable>
                        )
                    })}
                </View>
            </View>

            {demandesCochees.length > 0 && (
                <View style={styles.barreSelection}>
                    <Text style={styles.barreSelectionTexte}>
                        {demandesCochees.length} demande{demandesCochees.length > 1 ? 's' : ''} sélectionnée{demandesCochees.length > 1 ? 's' : ''}
                    </Text>
                    <Pressable onPress={toggleToutes} hitSlop={6}>
                        <Text style={styles.barreSelectionLien}>{toutesCochees ? 'Tout désélectionner' : `Tout sélectionner (${attenteVisibles.length})`}</Text>
                    </Pressable>
                    {!toutesCochees && (
                        <Pressable onPress={viderSelection} hitSlop={6}>
                            <Text style={styles.barreSelectionLien}>Annuler</Text>
                        </Pressable>
                    )}
                    <View style={styles.barreSelectionBoutons}>
                        <Pressable
                            onPress={ouvrirValidationGroupee}
                            style={[styles.boutonGroupe, { borderColor: couleursValideur.vertFg, backgroundColor: couleursValideur.vertBg }]}
                        >
                            <MaterialCommunityIcons name="check-all" size={15} color={couleursValideur.vertFg}/>
                            <Text style={[styles.carteBoutonTexte, { color: couleursValideur.vertFg }]}>Valider la sélection</Text>
                        </Pressable>
                        <Pressable
                            onPress={ouvrirRefusGroupe}
                            style={[styles.boutonGroupe, { borderColor: couleursValideur.danger, backgroundColor: couleursValideur.blanc }]}
                        >
                            <MaterialCommunityIcons name="close" size={15} color={couleursValideur.danger}/>
                            <Text style={[styles.carteBoutonTexte, { color: couleursValideur.danger }]}>Refuser la sélection</Text>
                        </Pressable>
                    </View>
                </View>
            )}

            {isResponsive ? (
                <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    snapToInterval={LARGEUR_COLONNE_MOBILE + 16}
                    decelerationRate="fast"
                    contentContainerStyle={styles.kanban}
                >
                    {contenuKanban}
                </ScrollView>
            ) : (
                <View style={styles.kanban}>{contenuKanban}</View>
            )}
        </ScrollView>
    )
}
